import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { StaffServicesDirectory } from './MyStaffServices';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SEO from '../components/SEO';
import { collectionPageLD, breadcrumbLD } from '../utils/structuredData';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Search,
  MapPin,
  Clock,
  DollarSign,
  CalendarIcon,
  Plus,
  Users,
  ChefHat,
  Wine,
  Briefcase,
  Wallet,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  X,
  Info,
  CreditCard,
  FileText,
  Trash2,
  Pencil,
  Bell,
  BellOff,
  Save,
  Mail,
  Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventismStaffing() {
  const { user, token, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('browse');
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPosition, setFilterPosition] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [applyingJob, setApplyingJob] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [reopenJob, setReopenJob] = useState(null);
  const [postJobOpen, setPostJobOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [partnerDashboard, setPartnerDashboard] = useState(null);
  const [myApplications, setMyApplications] = useState([]);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [depositPolling, setDepositPolling] = useState(false);
  const [staffPrefs, setStaffPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [notifLog, setNotifLog] = useState([]);
  // Application form (when a worker applies)
  const [applyDialog, setApplyDialog] = useState(null); // { job_id, title, position_label, base_rate, hours }
  const [applyForm, setApplyForm] = useState({ cover_letter: '', why_hire: '', experience: '', availability: '', phone: '', instagram: '', custom_answers: {} });
  // Applicants management (when poster reviews applications)
  const [applicantsDialog, setApplicantsDialog] = useState(null); // { job_id, title }
  const [applicantActionId, setApplicantActionId] = useState(null);

  const isPartner = user?.role === 'admin' || user?.role === 'b2b';

  const [newJob, setNewJob] = useState({
    title: '',
    description: '',
    position_type: 'foh',
    position_key: 'server',
    custom_position_label: '',
    location: '',
    date: '',
    start_time: '',
    end_time: '',
    base_rate: '',
    slots_needed: 1,
    requirements: '',
    event_name: '',
    event_id: '',
    featured_image_path: '',
    custom_questions: [],
  });
  const [featuredUploading, setFeaturedUploading] = useState(false);
  // Ask-a-question dialog
  const [askDialog, setAskDialog] = useState(null); // {job_id, title}
  const [askText, setAskText] = useState('');
  const [askSubmitting, setAskSubmitting] = useState(false);
  const [hostedEvents, setHostedEvents] = useState([]);

  const fetchPositions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staffing/positions`);
      setPositions(res.data);
    } catch (err) {
      console.error('Failed to fetch positions:', err);
    }
  }, []);

  const fetchHostedEvents = useCallback(async () => {
    if (!isPartner || !token) return;
    try {
      const res = await axios.get(`${API}/hosted-events`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data?.hosted_events || res.data?.events || res.data || [];
      setHostedEvents(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to fetch hosted events:', err);
    }
  }, [isPartner, token]);

  const fetchJobs = useCallback(async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.position_type = filterType;
      if (filterPosition !== 'all') params.position_key = filterPosition;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/staffing/jobs`, { params });
      setJobs(res.data.jobs);
      setTotalJobs(res.data.total);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterPosition, searchQuery]);

  const fetchPartnerDashboard = useCallback(async () => {
    if (!isPartner || !token) return;
    try {
      const res = await axios.get(`${API}/staffing/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartnerDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch partner dashboard:', err);
    }
  }, [isPartner, token]);

  const fetchMyApplications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/staffing/my-applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyApplications(res.data);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    }
  }, [token]);

  const fetchStaffPrefs = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/staffing/preferences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaffPrefs(res.data);
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    }
  }, [token]);

  const fetchNotifLog = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/staffing/notifications/log`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifLog(res.data);
    } catch (err) {
      console.error('Failed to fetch notification log:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  useEffect(() => {
    fetchHostedEvents();
  }, [fetchHostedEvents]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (activeTab === 'partner' && isPartner) fetchPartnerDashboard();
    if (activeTab === 'my-jobs') {
      fetchMyApplications();
      fetchStaffPrefs();
      fetchNotifLog();
    }
  }, [activeTab, isPartner, fetchPartnerDashboard, fetchMyApplications, fetchStaffPrefs, fetchNotifLog]);

  // Handle return from Stripe deposit checkout
  useEffect(() => {
    const depositSessionId = searchParams.get('deposit_session_id');
    const depositCancelled = searchParams.get('deposit_cancelled');
    
    if (depositCancelled) {
      toast.info('Deposit cancelled');
      setSearchParams({});
      return;
    }
    
    if (depositSessionId && token) {
      setDepositPolling(true);
      setWalletOpen(true);
      pollDepositStatus(depositSessionId, 0);
      setSearchParams({});
    }
  }, [searchParams, token]);

  // Deep-link from Production Manager: /staffing?post=1&event_id=xxx&event_name=...
  useEffect(() => {
    const shouldPost = searchParams.get('post');
    const evId = searchParams.get('event_id');
    const evName = searchParams.get('event_name');
    if (shouldPost && isPartner) {
      setNewJob((prev) => ({
        ...prev,
        event_id: evId || prev.event_id,
        event_name: evName || prev.event_name,
      }));
      setPostJobOpen(true);
      // Clear the query params so reopening is intentional
      const next = new URLSearchParams(searchParams);
      next.delete('post');
      next.delete('event_id');
      next.delete('event_name');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartner]);

  const pollDepositStatus = async (sessionId, attempt) => {
    const maxAttempts = 8;
    const pollInterval = 2500;
    
    if (attempt >= maxAttempts) {
      setDepositPolling(false);
      toast.error('Deposit status check timed out. Please check your wallet shortly.');
      return;
    }
    
    try {
      const res = await axios.get(`${API}/staffing/wallet/deposit-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.data.payment_status === 'paid') {
        setDepositPolling(false);
        toast.success('Deposit successful! Wallet credited.');
        fetchPartnerDashboard();
        return;
      } else if (res.data.status === 'expired') {
        setDepositPolling(false);
        toast.error('Payment session expired.');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollDepositStatus(sessionId, attempt + 1), pollInterval);
    } catch (err) {
      console.error('Deposit poll error:', err);
      setTimeout(() => pollDepositStatus(sessionId, attempt + 1), pollInterval);
    }
  };

  const handleSavePrefs = async (updatedPrefs) => {
    setSavingPrefs(true);
    try {
      const res = await axios.post(`${API}/staffing/preferences`, updatedPrefs, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaffPrefs(res.data.preferences);
      toast.success('Notification preferences saved!');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };


  const handlePostJob = async () => {
    if (!newJob.title || !newJob.date || !newJob.start_time || !newJob.end_time || !newJob.base_rate || !newJob.location) {
      toast.error('Please fill all required fields');
      return;
    }
    const isCustom = newJob.position_type === 'custom' || newJob.position_key === 'custom';
    if (isCustom && !(newJob.custom_position_label || '').trim()) {
      toast.error('Enter a title for your custom role');
      return;
    }
    try {
      const payload = {
        ...newJob,
        base_rate: parseFloat(newJob.base_rate),
        slots_needed: parseInt(newJob.slots_needed),
        requirements: newJob.requirements
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
      };
      if (!payload.event_id) delete payload.event_id;
      if (!isCustom) delete payload.custom_position_label;
      await axios.post(`${API}/staffing/jobs`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Job posted successfully!');
      setPostJobOpen(false);
      setNewJob({
        title: '', description: '', position_type: 'foh', position_key: 'server',
        custom_position_label: '',
        location: '', date: '', start_time: '', end_time: '',
        base_rate: '', slots_needed: 1, requirements: '', event_name: '', event_id: '',
        featured_image_path: '', custom_questions: [],
      });
      fetchJobs();
      if (isPartner) fetchPartnerDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post job');
    }
  };

  const openApply = (job) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to apply');
      return;
    }
    setApplyForm({
      cover_letter: '',
      why_hire: '',
      experience: '',
      availability: '',
      phone: user?.phone || '',
      instagram: '',
      custom_answers: {},
    });
    setApplyDialog({
      job_id: job.job_id,
      title: job.title,
      position_label: job.position_label,
      base_rate: job.base_rate,
      hours: job.hours,
      featured_image_path: job.featured_image_path,
      custom_questions: job.custom_questions || [],
    });
  };

  const submitApplication = async () => {
    if (!applyDialog) return;
    const jobId = applyDialog.job_id;
    // Validate required custom questions
    const cq = applyDialog.custom_questions || [];
    for (const q of cq) {
      if (q.required && !((applyForm.custom_answers || {})[q.id] || '').toString().trim()) {
        toast.error(`Please answer: "${q.label}"`);
        return;
      }
    }
    setApplyingJob(jobId);
    try {
      await axios.post(`${API}/staffing/jobs/${jobId}/apply`, applyForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Application submitted! The poster will review and reach out.');
      setApplyDialog(null);
      fetchJobs();
      if (activeTab === 'my-jobs') fetchMyApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingJob(null);
    }
  };

  const uploadFeaturedImage = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large (max 10 MB)');
      return;
    }
    setFeaturedUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewJob((j) => ({ ...j, featured_image_path: res.data.path }));
      toast.success('Featured image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setFeaturedUploading(false); }
  };

  const uploadApplicantFile = async (questionId, file) => {
    if (!file || !token) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10 MB)');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setApplyForm((f) => ({
        ...f,
        custom_answers: { ...(f.custom_answers || {}), [questionId]: `${API}/files/${res.data.path}|${file.name}` },
      }));
      toast.success('File uploaded');
    } catch (e) {
      toast.error('Upload failed');
    }
  };

  const addCustomQuestion = (type = 'short_text') => {
    setNewJob((j) => ({
      ...j,
      custom_questions: [
        ...(j.custom_questions || []),
        { id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type, label: '', required: false, options: [] },
      ],
    }));
  };
  const updateCustomQuestion = (id, patch) => {
    setNewJob((j) => ({
      ...j,
      custom_questions: (j.custom_questions || []).map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));
  };
  const removeCustomQuestion = (id) => {
    setNewJob((j) => ({ ...j, custom_questions: (j.custom_questions || []).filter((q) => q.id !== id) }));
  };

  const submitQuestion = async () => {
    if (!askDialog) return;
    if (!askText.trim()) { toast.error('Please type your question'); return; }
    setAskSubmitting(true);
    try {
      await axios.post(`${API}/staffing/jobs/${askDialog.job_id}/ask`, { question: askText }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Question sent — the poster has been emailed.');
      setAskDialog(null);
      setAskText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setAskSubmitting(false); }
  };

  const handleAcceptApplicant = async (jobId, applicantUserId) => {
    setApplicantActionId(applicantUserId + ':accept');
    try {
      await axios.post(`${API}/staffing/jobs/${jobId}/applicants/${applicantUserId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Applicant accepted');
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    } finally {
      setApplicantActionId(null);
    }
  };
  const handleRejectApplicant = async (jobId, applicantUserId) => {
    setApplicantActionId(applicantUserId + ':reject');
    try {
      await axios.post(`${API}/staffing/jobs/${jobId}/applicants/${applicantUserId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Applicant rejected');
      fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setApplicantActionId(null);
    }
  };

  const handleApply = async (jobId) => {
    // Legacy entrypoint — route to the new application dialog
    const job = jobs.find((j) => j.job_id === jobId);
    if (job) openApply(job);
  };

  const handleCancelJob = async (jobId) => {
    try {
      const res = await axios.delete(`${API}/staffing/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const refunded = res.data?.refunded || 0;
      toast.success(refunded > 0 ? `Job cancelled. $${refunded.toFixed(2)} refunded to wallet.` : 'Job cancelled');
      fetchJobs();
      if (isPartner) fetchPartnerDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel job');
    }
  };

  const handleCompleteJob = async (jobId) => {
    try {
      const res = await axios.post(`${API}/staffing/jobs/${jobId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      toast.success(
        `Job completed! ${d.payouts_created} payout(s) created ($${d.total_staff_payout.toFixed(2)} to staff).` +
        (d.unused_escrow_refunded > 0 ? ` $${d.unused_escrow_refunded.toFixed(2)} refunded.` : '')
      );
      fetchJobs();
      if (isPartner) fetchPartnerDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete job');
    }
  };

  const handleDeposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt < 25) {
      toast.error('Minimum deposit is $25');
      return;
    }
    setDepositing(true);
    try {
      const res = await axios.post(`${API}/staffing/wallet/deposit`, {
        amount: amt,
        origin_url: window.location.origin,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deposit failed');
      setDepositing(false);
    }
  };

  // Calculate cost preview for new job form
  const markup = positions?.default_markup || 5;
  const previewRate = parseFloat(newJob.base_rate) || 0;
  const previewTotal = previewRate + markup;
  const previewHours =
    newJob.start_time && newJob.end_time
      ? (() => {
          const [sh, sm] = newJob.start_time.split(':').map(Number);
          const [eh, em] = newJob.end_time.split(':').map(Number);
          let diff = (eh * 60 + em - (sh * 60 + sm)) / 60;
          if (diff <= 0) diff += 24;
          return diff;
        })()
      : 0;
  const previewSlots = parseInt(newJob.slots_needed) || 1;
  const previewStaffEarnings = previewRate * previewHours * previewSlots;
  const previewEstimate = previewTotal * previewHours * previewSlots;

  const positionTypeLabel = (type) => (type === 'foh' ? 'Front of House' : 'Back of House');
  const positionTypeColor = (type) =>
    type === 'foh'
      ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30'
      : 'bg-amber-500/20 text-amber-400 border-amber-500/30';

  // Update position_key when position_type changes
  const handlePositionTypeChange = (val) => {
    const first = positions?.positions?.[val]?.[0];
    setNewJob((prev) => ({
      ...prev,
      position_type: val,
      position_key: first?.key || 'custom',
      custom_position_label: first?.key === 'custom' ? (prev.custom_position_label || '') : '',
      base_rate: first?.default_rate?.toString() || '',
    }));
  };

  const handlePositionKeyChange = (val) => {
    const all = positions
      ? Object.values(positions.positions || {}).flat()
      : [];
    const pos = all.find((p) => p.key === val);
    setNewJob((prev) => ({
      ...prev,
      position_key: val,
      base_rate: pos?.default_rate?.toString() || prev.base_rate,
    }));
  };

  const handleEventSelect = (val) => {
    if (val === '__none__') {
      setNewJob((prev) => ({ ...prev, event_id: '', event_name: '' }));
      return;
    }
    const ev = hostedEvents.find((e) => e.event_id === val);
    setNewJob((prev) => ({
      ...prev,
      event_id: val,
      event_name: ev?.title || ev?.slug || prev.event_name,
    }));
  };

  const allPositionsList = positions
    ? [...positions.positions.foh, ...positions.positions.boh]
    : [];

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <SEO
        title="Event Staff Jobs & Hiring — Bartenders, Servers, DJs, Models, Marketing | Eventism"
        description="Hire event staff or get hired for shifts in Denver, Las Vegas, Los Angeles & Charlotte. Bartenders, servers, security, brand ambassadors, DJs, performers, photographers, marketers and more — escrow-protected pay, instant booking."
        keywords={[
          'event staff jobs',
          'bartender jobs',
          'server jobs',
          'event security jobs',
          'brand ambassador jobs',
          'DJ jobs',
          'event staffing marketplace',
          'hire event staff',
          'gig work events',
          'Denver event jobs',
          'Las Vegas event jobs',
          'Los Angeles event jobs',
          'Charlotte event jobs',
          'event gigs',
          'event freelance',
          'Eventism staffing',
        ]}
        structuredData={[
          collectionPageLD('Event Staff Jobs & Hiring', 'Marketplace for hourly event staff — bartenders, servers, security, performers, marketers, and more.'),
          breadcrumbLD([
            { name: 'Home', url: '/' },
            { name: 'Event Staffing', url: '/shifts' },
          ]),
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Header */}
        <div className="relative mb-10">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/5 to-transparent rounded-2xl blur-3xl -z-10" />
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-indigo-400" />
                </div>
                <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/20 text-xs tracking-wide">
                  MARKETPLACE
                </Badge>
              </div>
              <h1 className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-2" data-testid="staffing-title">
                Eventism Staffing
              </h1>
              <p className="text-muted-foreground text-base max-w-xl">
                Hospitality staffing marketplace. Post FOH & BOH positions or find your next gig.
              </p>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              {isPartner && (
                <>
                  <Button
                    variant="outline"
                    className="border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                    onClick={() => setWalletOpen(true)}
                    data-testid="wallet-btn"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Wallet{partnerDashboard?.wallet ? `: $${partnerDashboard.wallet.balance?.toFixed(2)}` : ''}
                  </Button>
                  <Button
                    className="bg-indigo-500 hover:bg-indigo-600 text-foreground font-semibold transition-all"
                    onClick={() => setPostJobOpen(true)}
                    data-testid="post-job-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Post a Job
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-card border border-border p-1">
            <TabsTrigger value="browse" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-foreground" data-testid="tab-browse">
              Browse Jobs
            </TabsTrigger>
            {isAuthenticated && (
              <TabsTrigger value="my-jobs" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-foreground" data-testid="tab-my-jobs">
                My Applications
              </TabsTrigger>
            )}
            {isPartner && (
              <TabsTrigger value="partner" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-foreground" data-testid="tab-partner">
                Partner Dashboard
              </TabsTrigger>
            )}
          </TabsList>

          {/* Browse Jobs Tab */}
          <TabsContent value="browse" className="space-y-6">
            {/* Staff services directory — self-published listings */}
            <StaffServicesDirectory city="denver" limit={4} />

            {/* Filters */}
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  placeholder="Search by title, location, position..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 bg-card border-border text-foreground placeholder:text-muted-foreground focus:border-indigo-500/50"
                  data-testid="staffing-search"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  onClick={() => { setFilterType('all'); setFilterPosition('all'); }}
                  className={filterType === 'all' ? 'bg-indigo-500 hover:bg-indigo-600' : 'border-border text-muted-foreground hover:bg-muted'}
                  data-testid="filter-all"
                >
                  All Positions
                </Button>
                <Button
                  size="sm"
                  variant={filterType === 'foh' ? 'default' : 'outline'}
                  onClick={() => { setFilterType('foh'); setFilterPosition('all'); }}
                  className={filterType === 'foh' ? 'bg-indigo-500 hover:bg-indigo-600' : 'border-border text-muted-foreground hover:bg-muted'}
                  data-testid="filter-foh"
                >
                  <Wine className="h-3.5 w-3.5 mr-1.5" />
                  Front of House
                </Button>
                <Button
                  size="sm"
                  variant={filterType === 'boh' ? 'default' : 'outline'}
                  onClick={() => { setFilterType('boh'); setFilterPosition('all'); }}
                  className={filterType === 'boh' ? 'bg-amber-500 hover:bg-amber-600' : 'border-border text-muted-foreground hover:bg-muted'}
                  data-testid="filter-boh"
                >
                  <ChefHat className="h-3.5 w-3.5 mr-1.5" />
                  Back of House
                </Button>
                {filterType !== 'all' && positions?.positions?.[filterType] && (
                  <div className="flex gap-1 ml-2 pl-2 border-l border-border">
                    {positions.positions[filterType].map((p) => (
                      <Button
                        key={p.key}
                        size="sm"
                        variant={filterPosition === p.key ? 'default' : 'outline'}
                        onClick={() => setFilterPosition(filterPosition === p.key ? 'all' : p.key)}
                        className={filterPosition === p.key ? 'bg-accent' : 'border-border text-muted-foreground hover:bg-muted text-xs'}
                        data-testid={`filter-pos-${p.key}`}
                      >
                        {p.label}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Jobs Grid */}
            {loading ? (
              <div className="text-center py-16">
                <div className="animate-pulse text-muted-foreground">Loading jobs...</div>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-16">
                <Briefcase className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-['Space_Grotesk'] text-xl font-semibold text-foreground mb-2">No jobs available</h3>
                <p className="text-muted-foreground">Check back soon or adjust your filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="jobs-grid">
                {jobs.map((job) => (
                  <JobCard
                    key={job.job_id}
                    job={job}
                    onApply={handleApply}
                    applyingJob={applyingJob}
                    isPartner={isPartner}
                    currentUserId={user?.user_id}
                    onCancel={handleCancelJob}
                    onComplete={handleCompleteJob}
                    onEdit={setEditJob}
                    onReopen={setReopenJob}
                    onViewApplicants={setApplicantsDialog}
                    onAsk={(j) => { if (!isAuthenticated) { toast.error('Please sign in to ask a question'); return; } setAskDialog({ job_id: j.job_id, title: j.title }); }}
                    positionTypeColor={positionTypeColor}
                    positionTypeLabel={positionTypeLabel}
                  />
                ))}
              </div>
            )}
            {totalJobs > 0 && (
              <p className="text-center text-sm text-muted-foreground">{totalJobs} job{totalJobs !== 1 ? 's' : ''} found</p>
            )}
          </TabsContent>

          {/* My Applications Tab */}
          <TabsContent value="my-jobs" className="space-y-8">
            {/* Notification Preferences */}
            <StaffPreferencesPanel
              prefs={staffPrefs}
              positions={positions}
              saving={savingPrefs}
              onSave={handleSavePrefs}
              notifLog={notifLog}
            />

            {/* Applied Jobs */}
            <div>
              <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-foreground mb-4">My Applications</h3>
              {myApplications.length === 0 ? (
                <div className="text-center py-12 bg-muted/50 rounded-xl border border-border">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-foreground font-semibold mb-1">No applications yet</p>
                  <p className="text-muted-foreground text-sm mb-4">Browse available jobs and apply to get started</p>
                  <Button onClick={() => setActiveTab('browse')} size="sm" className="bg-indigo-500 hover:bg-indigo-600">
                    Browse Jobs <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {myApplications.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onApply={handleApply}
                      applyingJob={applyingJob}
                      isPartner={isPartner}
                      currentUserId={user?.user_id}
                      onCancel={handleCancelJob}
                      onComplete={handleCompleteJob}
                      onEdit={setEditJob}
                      onReopen={setReopenJob}
                      onViewApplicants={setApplicantsDialog}
                      onAsk={(j) => setAskDialog({ job_id: j.job_id, title: j.title })}
                      positionTypeColor={positionTypeColor}
                      positionTypeLabel={positionTypeLabel}
                      showApplicationStatus
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Partner Dashboard Tab */}
          {isPartner && (
            <TabsContent value="partner" className="space-y-6">
              <PartnerDashboardView
                data={partnerDashboard}
                onPostJob={() => setPostJobOpen(true)}
                onOpenWallet={() => setWalletOpen(true)}
                onCancelJob={handleCancelJob}
                onCompleteJob={handleCompleteJob}
                positionTypeColor={positionTypeColor}
                positionTypeLabel={positionTypeLabel}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* Post Job Dialog */}
        <Dialog open={postJobOpen} onOpenChange={setPostJobOpen}>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-foreground font-['Space_Grotesk'] text-xl">Post a Staffing Job</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Category *</Label>
                  <Select value={newJob.position_type} onValueChange={handlePositionTypeChange}>
                    <SelectTrigger className="bg-muted border-border text-foreground" data-testid="select-position-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {(positions?.position_types || [
                        { key: 'foh', label: 'Front of House' },
                        { key: 'boh', label: 'Back of House' },
                      ]).map((pt) => (
                        <SelectItem key={pt.key} value={pt.key} className="text-foreground">
                          {pt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Position *</Label>
                  <Select value={newJob.position_key} onValueChange={handlePositionKeyChange}>
                    <SelectTrigger className="bg-muted border-border text-foreground" data-testid="select-position-key">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border max-h-72">
                      {positions?.positions?.[newJob.position_type]?.map((p) => (
                        <SelectItem key={p.key} value={p.key} className="text-foreground">
                          {p.label}{p.key !== 'custom' ? ` ($${p.default_rate}/hr suggested)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(newJob.position_type === 'custom' || newJob.position_key === 'custom') && (
                <div className="space-y-2" data-testid="custom-role-row">
                  <Label className="text-muted-foreground text-sm">Custom Role Title *</Label>
                  <Input
                    value={newJob.custom_position_label}
                    onChange={(e) => setNewJob({ ...newJob, custom_position_label: e.target.value })}
                    placeholder='e.g. "Body Painter", "Silent-Disco Host", "Fire Performer"'
                    className="bg-muted border-border text-foreground"
                    data-testid="input-custom-position"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Describe the role exactly as you want staff to see it — this appears in listings & notifications.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Link to an Event (optional)</Label>
                <Select value={newJob.event_id || '__none__'} onValueChange={handleEventSelect}>
                  <SelectTrigger className="bg-muted border-border text-foreground" data-testid="select-event">
                    <SelectValue placeholder="Not linked to an event" />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border max-h-72">
                    <SelectItem value="__none__" className="text-muted-foreground">— No event link —</SelectItem>
                    {hostedEvents.map((ev) => (
                      <SelectItem key={ev.event_id} value={ev.event_id} className="text-foreground">
                        {ev.title || ev.slug || ev.event_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Linked jobs show up in that event's Production Manager → Staff tab.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Job Title *</Label>
                <Input
                  value={newJob.title}
                  onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                  placeholder="e.g. Bartender for Corporate Gala"
                  className="bg-muted border-border text-foreground"
                  data-testid="input-job-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Location *</Label>
                  <Input
                    value={newJob.location}
                    onChange={(e) => setNewJob({ ...newJob, location: e.target.value })}
                    placeholder="Downtown LA"
                    className="bg-muted border-border text-foreground"
                    data-testid="input-location"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Event Name {newJob.event_id ? '(auto-filled)' : '(optional)'}</Label>
                  <Input
                    value={newJob.event_name}
                    onChange={(e) => setNewJob({ ...newJob, event_name: e.target.value })}
                    placeholder="Optional"
                    className="bg-muted border-border text-foreground"
                    disabled={!!newJob.event_id}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Date *</Label>
                  <Input
                    type="date"
                    value={newJob.date}
                    onChange={(e) => setNewJob({ ...newJob, date: e.target.value })}
                    className="bg-muted border-border text-foreground"
                    data-testid="input-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Start Time *</Label>
                  <Input
                    type="time"
                    value={newJob.start_time}
                    onChange={(e) => setNewJob({ ...newJob, start_time: e.target.value })}
                    className="bg-muted border-border text-foreground"
                    data-testid="input-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">End Time *</Label>
                  <Input
                    type="time"
                    value={newJob.end_time}
                    onChange={(e) => setNewJob({ ...newJob, end_time: e.target.value })}
                    className="bg-muted border-border text-foreground"
                    data-testid="input-end-time"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Staff Pay Rate ($/hr) *</Label>
                  <Input
                    type="number"
                    step="0.50"
                    value={newJob.base_rate}
                    onChange={(e) => setNewJob({ ...newJob, base_rate: e.target.value })}
                    placeholder="22.00"
                    className="bg-muted border-border text-foreground"
                    data-testid="input-base-rate"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm">Workers Needed *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newJob.slots_needed}
                    onChange={(e) => setNewJob({ ...newJob, slots_needed: e.target.value })}
                    className="bg-muted border-border text-foreground"
                    data-testid="input-slots"
                  />
                </div>
              </div>

              {/* Featured image upload */}
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Featured image (optional)</Label>
                <p className="text-[11px] text-muted-foreground -mt-1">Make your listing pop — recommended 1200×630, max 10 MB.</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => uploadFeaturedImage(e.target.files?.[0])}
                      data-testid="featured-image-input"
                      disabled={featuredUploading}
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/30 text-sm">
                      {featuredUploading ? 'Uploading…' : (newJob.featured_image_path ? 'Replace image' : 'Upload featured image')}
                    </span>
                  </label>
                  {newJob.featured_image_path && (
                    <div className="flex items-center gap-2">
                      <img src={`${API}/files/${newJob.featured_image_path}`} alt="preview" className="h-14 w-14 object-cover rounded-lg border border-border" />
                      <button type="button" onClick={() => setNewJob((j) => ({ ...j, featured_image_path: '' }))} className="text-xs text-red-400 hover:underline" data-testid="remove-featured-image">remove</button>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom application questions */}
              <div className="space-y-2 border-t border-border/40 pt-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-muted-foreground text-sm">Custom application questions (optional)</Label>
                    <p className="text-[11px] text-muted-foreground">Ask applicants anything — Instagram, photo upload, dietary cert, dress size…</p>
                  </div>
                  <Select onValueChange={addCustomQuestion}>
                    <SelectTrigger className="w-44 h-9 bg-muted border-border text-foreground text-xs" data-testid="add-custom-question">
                      <SelectValue placeholder="+ Add question" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="short_text">Short text</SelectItem>
                      <SelectItem value="long_text">Long text</SelectItem>
                      <SelectItem value="url">URL / link</SelectItem>
                      <SelectItem value="file">File / photo upload</SelectItem>
                      <SelectItem value="yes_no">Yes / no</SelectItem>
                      <SelectItem value="dropdown">Dropdown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(newJob.custom_questions || []).map((q, idx) => (
                  <div key={q.id} className="bg-muted/40 border border-border/40 rounded-lg p-3 space-y-2" data-testid={`cq-row-${idx}`}>
                    <div className="flex items-start gap-2">
                      <Input
                        value={q.label}
                        onChange={(e) => updateCustomQuestion(q.id, { label: e.target.value })}
                        placeholder="Question label (e.g. Instagram handle)"
                        className="bg-card border-border text-foreground flex-1"
                        data-testid={`cq-label-${idx}`}
                      />
                      <Badge variant="outline" className="text-xs whitespace-nowrap mt-2">{q.type.replace('_', ' ')}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => removeCustomQuestion(q.id)} className="text-red-400 mt-1" data-testid={`cq-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                    {q.type === 'dropdown' && (
                      <Input
                        value={(q.options || []).join(', ')}
                        onChange={(e) => updateCustomQuestion(q.id, { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })}
                        placeholder="Comma-separated options (e.g. XS, S, M, L, XL)"
                        className="bg-card border-border text-foreground"
                        data-testid={`cq-options-${idx}`}
                      />
                    )}
                    <Input
                      value={q.help_text || ''}
                      onChange={(e) => updateCustomQuestion(q.id, { help_text: e.target.value })}
                      placeholder="Help text (optional)"
                      className="bg-card border-border text-foreground text-xs"
                    />
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={!!q.required} onChange={(e) => updateCustomQuestion(q.id, { required: e.target.checked })} data-testid={`cq-required-${idx}`} />
                      Required
                    </label>
                  </div>
                ))}
              </div>

              {/* Cost Breakdown Preview */}
              {previewRate > 0 && (
                <div className="bg-muted/60 border border-border/50 rounded-xl p-4 space-y-2" data-testid="cost-preview">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="h-4 w-4 text-indigo-400" />
                    <span className="text-sm font-semibold text-muted-foreground">Cost Breakdown</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Staff pay rate:</span>
                      <span className="text-foreground">${previewRate.toFixed(2)}/hr</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Eventism fee:</span>
                      <span className="text-indigo-400">+${markup.toFixed(2)}/hr</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Your cost per hour:</span>
                      <span className="font-semibold text-foreground">${previewTotal.toFixed(2)}/hr</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Shift duration:</span>
                      <span className="text-foreground">{previewHours > 0 ? `${previewHours.toFixed(1)} hrs` : '--'}</span>
                    </div>
                  </div>
                  {previewEstimate > 0 && (
                    <div className="border-t border-border/50 pt-2 mt-2 space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground font-semibold">Staff will earn:</span>
                        <span className="text-xl font-bold text-lime-400" data-testid="estimated-staff-earnings">
                          ${previewStaffEarnings.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">Your total cost (incl. fees):</span>
                        <span className="text-sm font-semibold text-foreground" data-testid="estimated-total">
                          ${previewEstimate.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Requirements (comma-separated)</Label>
                <Input
                  value={newJob.requirements}
                  onChange={(e) => setNewJob({ ...newJob, requirements: e.target.value })}
                  placeholder="Food handler cert, Black attire"
                  className="bg-muted border-border text-foreground"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Description</Label>
                <Textarea
                  value={newJob.description}
                  onChange={(e) => setNewJob({ ...newJob, description: e.target.value })}
                  placeholder="Describe the role, expectations, dress code..."
                  className="bg-muted border-border text-foreground"
                  rows={3}
                />
              </div>

              <Button onClick={handlePostJob} className="w-full bg-indigo-500 hover:bg-indigo-600 font-semibold h-11" data-testid="submit-job-btn">
                Post Job
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Wallet Dialog */}
        <Dialog open={walletOpen} onOpenChange={(open) => { if (!depositPolling) setWalletOpen(open); }}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="text-foreground font-['Space_Grotesk']">Staffing Wallet</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-2">
              {depositPolling ? (
                <div className="text-center py-6">
                  <div className="h-12 w-12 rounded-full bg-indigo-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <CreditCard className="h-6 w-6 text-indigo-400" />
                  </div>
                  <p className="text-foreground font-semibold mb-1">Processing your deposit...</p>
                  <p className="text-sm text-muted-foreground">Verifying payment with Stripe</p>
                </div>
              ) : (
                <>
                  <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-xl p-5 text-center">
                    <p className="text-sm text-muted-foreground mb-1">Available Balance</p>
                    <p className="text-4xl font-bold text-foreground font-['Space_Grotesk']" data-testid="wallet-balance">
                      ${partnerDashboard?.wallet?.balance?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <Label className="text-muted-foreground text-sm">Deposit Funds via Stripe</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                        placeholder="Amount ($25 min)"
                        className="bg-muted border-border text-foreground"
                        data-testid="deposit-amount"
                      />
                      <Button
                        onClick={handleDeposit}
                        disabled={depositing}
                        className="bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold px-6"
                        data-testid="deposit-btn"
                      >
                        <CreditCard className="h-4 w-4 mr-2" />
                        {depositing ? 'Redirecting...' : 'Pay'}
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      {[50, 100, 250, 500].map((amt) => (
                        <Button
                          key={amt}
                          size="sm"
                          variant="outline"
                          className="border-border text-muted-foreground text-xs flex-1"
                          onClick={() => setDepositAmount(amt.toString())}
                        >
                          ${amt}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Info className="h-3 w-3" /> You'll be redirected to Stripe to complete payment
                    </p>
                  </div>
                  {partnerDashboard?.wallet && (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-muted/60 rounded-lg p-3">
                        <p className="text-muted-foreground text-xs">Total Deposited</p>
                        <p className="text-foreground font-semibold">${partnerDashboard.wallet.total_deposited?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="bg-muted/60 rounded-lg p-3">
                        <p className="text-muted-foreground text-xs">Total Spent</p>
                        <p className="text-foreground font-semibold">${partnerDashboard.wallet.total_spent?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit job dialog */}
        {editJob && (
          <EditJobDialog
            job={editJob}
            token={token}
            hostedEvents={hostedEvents}
            onClose={(updated) => {
              setEditJob(null);
              if (updated) { fetchJobs(); if (isPartner) fetchPartnerDashboard(); }
            }}
          />
        )}

        {/* Reopen cancelled job dialog */}
        {reopenJob && (
          <EditJobDialog
            mode="reopen"
            job={reopenJob}
            token={token}
            hostedEvents={hostedEvents}
            onClose={(updated) => {
              setReopenJob(null);
              if (updated) { fetchJobs(); if (isPartner) fetchPartnerDashboard(); }
            }}
          />
        )}

        {/* Apply Dialog — worker submits an application form */}
        <Dialog open={!!applyDialog} onOpenChange={(o) => !o && setApplyDialog(null)}>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Apply for: {applyDialog?.title}</DialogTitle>
            </DialogHeader>
            {applyDialog && (
              <div className="space-y-3 py-2">
                <div className="rounded-lg bg-lime-500/10 border border-lime-500/30 p-3 text-xs">
                  <strong className="text-lime-400">{applyDialog.position_label}</strong> · ${applyDialog.base_rate}/hr · {applyDialog.hours} hrs
                  <div className="text-muted-foreground mt-0.5">You'll earn ${(applyDialog.base_rate * applyDialog.hours).toFixed(2)} if selected.</div>
                </div>
                <div>
                  <Label htmlFor="apply-cover">Cover letter / introduction *</Label>
                  <Textarea
                    id="apply-cover"
                    rows={4}
                    value={applyForm.cover_letter}
                    onChange={(e) => setApplyForm({ ...applyForm, cover_letter: e.target.value })}
                    placeholder="Tell the poster a bit about yourself and why this gig interests you."
                    data-testid="apply-cover-letter"
                  />
                </div>
                <div>
                  <Label htmlFor="apply-why">Why should they hire you?</Label>
                  <Textarea
                    id="apply-why"
                    rows={3}
                    value={applyForm.why_hire}
                    onChange={(e) => setApplyForm({ ...applyForm, why_hire: e.target.value })}
                    placeholder="What makes you the right fit?"
                    data-testid="apply-why-hire"
                  />
                </div>
                <div>
                  <Label htmlFor="apply-exp">Relevant experience</Label>
                  <Textarea
                    id="apply-exp"
                    rows={3}
                    value={applyForm.experience}
                    onChange={(e) => setApplyForm({ ...applyForm, experience: e.target.value })}
                    placeholder="Past gigs, certifications, references…"
                    data-testid="apply-experience"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="apply-avail">Availability confirmation</Label>
                    <Input
                      id="apply-avail"
                      value={applyForm.availability}
                      onChange={(e) => setApplyForm({ ...applyForm, availability: e.target.value })}
                      placeholder="e.g. Confirmed available the full shift"
                      data-testid="apply-availability"
                    />
                  </div>
                  <div>
                    <Label htmlFor="apply-phone">Phone</Label>
                    <Input
                      id="apply-phone"
                      value={applyForm.phone}
                      onChange={(e) => setApplyForm({ ...applyForm, phone: e.target.value })}
                      placeholder="So the poster can reach you"
                      data-testid="apply-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="apply-ig">Instagram / Portfolio link</Label>
                  <Input
                    id="apply-ig"
                    value={applyForm.instagram}
                    onChange={(e) => setApplyForm({ ...applyForm, instagram: e.target.value })}
                    placeholder="https://instagram.com/yourhandle"
                    data-testid="apply-instagram"
                  />
                </div>
                {/* Custom questions from poster */}
                {(applyDialog.custom_questions || []).length > 0 && (
                  <div className="space-y-3 pt-2 border-t border-border/40">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Additional questions from the poster</p>
                    {(applyDialog.custom_questions || []).map((q) => {
                      const v = (applyForm.custom_answers || {})[q.id] || '';
                      const setVal = (val) => setApplyForm((f) => ({ ...f, custom_answers: { ...(f.custom_answers || {}), [q.id]: val } }));
                      const lbl = `${q.label}${q.required ? ' *' : ''}`;
                      return (
                        <div key={q.id} data-testid={`custom-q-${q.id}`}>
                          <Label>{lbl}</Label>
                          {q.help_text && <p className="text-[11px] text-muted-foreground mb-1">{q.help_text}</p>}
                          {q.type === 'long_text' && (
                            <Textarea rows={3} value={v} onChange={(e) => setVal(e.target.value)} />
                          )}
                          {q.type === 'short_text' && (
                            <Input value={v} onChange={(e) => setVal(e.target.value)} />
                          )}
                          {q.type === 'url' && (
                            <Input type="url" value={v} onChange={(e) => setVal(e.target.value)} placeholder="https://" />
                          )}
                          {q.type === 'yes_no' && (
                            <Select value={v} onValueChange={setVal}>
                              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {q.type === 'dropdown' && (
                            <Select value={v} onValueChange={setVal}>
                              <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                              <SelectContent>
                                {(q.options || []).map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          {q.type === 'file' && (
                            <div>
                              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => uploadApplicantFile(q.id, e.target.files?.[0])} />
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/30">
                                  Upload file/photo
                                </span>
                                {v && (() => {
                                  const [url, fname] = String(v).split('|');
                                  return <a href={url} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 hover:underline">{fname || 'uploaded'}</a>;
                                })()}
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyDialog(null)}>Cancel</Button>
              <Button
                className="bg-lime-500 hover:bg-lime-600 text-slate-900"
                onClick={submitApplication}
                disabled={applyingJob === applyDialog?.job_id || !applyForm.cover_letter.trim()}
                data-testid="submit-application-btn"
              >
                {applyingJob === applyDialog?.job_id ? 'Submitting…' : 'Submit application'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Applicants Dialog — poster reviews & picks who to hire */}
        <Dialog open={!!applicantsDialog} onOpenChange={(o) => !o && setApplicantsDialog(null)}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="applicants-dialog">
            <DialogHeader>
              <DialogTitle>Applicants for: {applicantsDialog?.title}</DialogTitle>
            </DialogHeader>
            {(() => {
              const job = jobs.find((j) => j.job_id === applicantsDialog?.job_id);
              const applicants = job?.applicants || [];
              if (!applicants.length) {
                return <p className="py-6 text-center text-sm text-muted-foreground">No applicants yet.</p>;
              }
              return (
                <div className="space-y-3 py-2">
                  {applicants.map((a) => (
                    <div key={a.user_id} className="border rounded-lg p-3" data-testid={`applicant-${a.user_id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold">{a.name}</h4>
                            <Badge
                              variant="outline"
                              className={
                                a.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' :
                                a.status === 'rejected' ? 'bg-red-500/20 text-red-400 border-red-500/40' :
                                'bg-amber-500/20 text-amber-400 border-amber-500/40'
                              }
                            >{a.status}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground mt-1">
                            {a.email && <a href={`mailto:${a.email}`} className="hover:underline hover:text-indigo-400">{a.email}</a>}
                            {a.phone && <a href={`tel:${a.phone}`} className="hover:underline hover:text-fuchsia-400">{a.phone}</a>}
                          </div>
                          {a.cover_letter && (
                            <div className="mt-2">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Cover letter</p>
                              <p className="text-sm">{a.cover_letter}</p>
                            </div>
                          )}
                          {a.why_hire && (
                            <div className="mt-2">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Why hire</p>
                              <p className="text-sm">{a.why_hire}</p>
                            </div>
                          )}
                          {a.experience && (
                            <div className="mt-2">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Experience</p>
                              <p className="text-sm">{a.experience}</p>
                            </div>
                          )}
                          {a.availability && (
                            <div className="mt-2 text-xs"><strong>Availability:</strong> {a.availability}</div>
                          )}
                          <p className="text-[10px] text-muted-foreground mt-2">Applied {a.applied_at ? new Date(a.applied_at).toLocaleString() : ''}</p>
                        </div>
                        {a.status === 'pending' && (
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-semibold"
                              onClick={() => handleAcceptApplicant(applicantsDialog.job_id, a.user_id)}
                              disabled={applicantActionId === a.user_id + ':accept'}
                              data-testid={`accept-applicant-${a.user_id}`}
                            >
                              {applicantActionId === a.user_id + ':accept' ? 'Accepting…' : 'Accept & Hire'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                              onClick={() => handleRejectApplicant(applicantsDialog.job_id, a.user_id)}
                              disabled={applicantActionId === a.user_id + ':reject'}
                              data-testid={`reject-applicant-${a.user_id}`}
                            >
                              {applicantActionId === a.user_id + ':reject' ? 'Rejecting…' : 'Reject'}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplicantsDialog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ask a Question Dialog */}
        <Dialog open={!!askDialog} onOpenChange={(o) => !o && setAskDialog(null)}>
          <DialogContent className="max-w-md" data-testid="ask-question-dialog">
            <DialogHeader>
              <DialogTitle>Ask a question about: {askDialog?.title}</DialogTitle>
            </DialogHeader>
            <div className="py-2">
              <Label htmlFor="ask-text">Your question *</Label>
              <Textarea
                id="ask-text"
                rows={5}
                value={askText}
                onChange={(e) => setAskText(e.target.value)}
                placeholder="Hi! I had a question about the shift…"
                data-testid="ask-text-input"
              />
              <p className="text-[11px] text-muted-foreground mt-2">The poster will get an email with your question and can reply directly.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAskDialog(null)}>Cancel</Button>
              <Button onClick={submitQuestion} disabled={askSubmitting || !askText.trim()} className="bg-indigo-500 hover:bg-indigo-600" data-testid="ask-submit-btn">
                {askSubmitting ? 'Sending…' : 'Send question'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

// ===== Job Card Component =====
function JobCard({ job, onApply, onAsk, applyingJob, isPartner, currentUserId, onCancel, onComplete, onEdit, onReopen, onViewApplicants, positionTypeColor, positionTypeLabel, showApplicationStatus }) {
  const isOwnPost = currentUserId === job.posted_by;
  const hasApplied = job.applicants?.some((a) => a.user_id === currentUserId);
  const isFilled = job.slots_filled >= job.slots_needed;

  return (
    <Card
      className="bg-card/80 border-border hover:border-indigo-500/30 transition-all duration-300 group"
      data-testid={`job-card-${job.job_id}`}
    >
      <CardContent className="p-0">
        {job.featured_image_path && (
          <div className="relative">
            <img
              src={`${API}/files/${job.featured_image_path}`}
              alt={job.title}
              className="w-full h-40 object-cover"
              data-testid={`job-featured-image-${job.job_id}`}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-card to-transparent" />
          </div>
        )}
        <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${positionTypeColor(job.position_type)} text-xs`}>
                {positionTypeLabel(job.position_type)}
              </Badge>
              <Badge variant="outline" className="text-xs border-border text-muted-foreground">
                {job.position_label}
              </Badge>
              {job.event_name && (
                <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                  {job.event_name}
                </Badge>
              )}
            </div>
            <Link
              to={`/staffing/jobs/${job.job_id}`}
              className="block font-['Space_Grotesk'] text-lg font-semibold text-foreground hover:text-indigo-300 transition-colors"
              data-testid={`job-title-${job.job_id}`}
            >
              {job.title}
            </Link>
          </div>
          <div className="text-right flex-shrink-0 ml-3">
            <p className="text-2xl font-bold text-lime-400 font-['Space_Grotesk']">${job.base_rate}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">per hour</p>
          </div>
        </div>

        {job.description && (
          <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{job.description}</p>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-2.5 mb-3 text-sm">
          <div className="flex items-center text-muted-foreground">
            <CalendarIcon className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            {new Date(job.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
          </div>
          <div className="flex items-center text-muted-foreground">
            <Clock className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            {job.start_time} - {job.end_time} ({job.hours}h)
          </div>
          <div className="flex items-center text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            {job.location}
          </div>
          <div className="flex items-center text-muted-foreground">
            <Users className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
            {job.slots_filled}/{job.slots_needed} filled
          </div>
        </div>

        {/* Requirements */}
        {job.requirements?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {job.requirements.map((req, i) => (
              <Badge key={i} variant="outline" className="text-[11px] border-border/60 text-muted-foreground">
                {req}
              </Badge>
            ))}
          </div>
        )}

        {/* Partner Cost Breakdown (only visible to partners) */}
        {isPartner && isOwnPost && (
          <div className="bg-muted/40 rounded-lg p-2.5 mb-3 text-xs">
            <div className="flex justify-between text-muted-foreground">
              <span>Staff rate: ${job.base_rate}/hr</span>
              <span>+ ${job.eventism_markup}/hr fee</span>
              <span className="text-foreground font-semibold">Total: ${job.total_hourly_cost}/hr</span>
            </div>
            <div className="flex justify-between text-muted-foreground mt-1">
              <span>Staff earns: <span className="text-lime-400 font-semibold">${((job.worker_earnings_per_worker ?? (job.base_rate * job.hours)) * job.slots_needed).toFixed(2)}</span></span>
              <span>Your cost: <span className="text-foreground font-semibold">${job.estimated_total?.toFixed(2)}</span></span>
            </div>
          </div>
        )}

        {/* Escrow Info */}
        {isPartner && isOwnPost && job.escrow_amount > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-amber-400 mb-2">
            <Wallet className="h-3 w-3" />
            <span>${job.escrow_amount?.toFixed(2)} held in escrow</span>
          </div>
        )}

        {/* Poster Info */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
          <span>Posted by {job.poster_name}</span>
          {showApplicationStatus && (
            <Badge className="bg-lime-500/20 text-lime-400 border-lime-500/30 text-xs">Applied</Badge>
          )}
          {job.status === 'completed' && (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">Completed</Badge>
          )}
        </div>

        {/* Action Button */}
        {job.status === 'completed' ? (
          <Button className="w-full bg-muted text-emerald-400 cursor-default" disabled>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Completed
          </Button>
        ) : isOwnPost ? (
          <div className="flex gap-2 flex-wrap">
            {(job.applicants || []).filter((a) => a.status !== 'rejected').length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10"
                onClick={() => onViewApplicants && onViewApplicants({ job_id: job.job_id, title: job.title })}
                data-testid={`view-applicants-${job.job_id}`}
              >
                <Users className="h-3.5 w-3.5 mr-1.5" />
                Review Applicants ({(job.applicants || []).filter((a) => a.status === 'pending').length} new / {(job.applicants || []).length} total)
              </Button>
            )}
            {(job.status === 'open' || job.status === 'filled') && job.slots_filled > 0 && (
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-foreground font-semibold"
                onClick={() => onComplete(job.job_id)}
                data-testid={`complete-job-${job.job_id}`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Complete Job
              </Button>
            )}
            {(job.applicants || []).length === 0 && (
              <Button variant="outline" size="sm" className="flex-1 border-border text-muted-foreground" disabled>
                Awaiting Applicants
              </Button>
            )}
            {(job.status === 'open' || job.status === 'filled') && (
              <Button
                variant="outline"
                size="sm"
                className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10"
                onClick={() => onEdit && onEdit(job)}
                data-testid={`edit-job-${job.job_id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {(job.status === 'open' || job.status === 'filled') && (
              <Button
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={() => onCancel(job.job_id)}
                data-testid={`cancel-job-${job.job_id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            {job.status === 'cancelled' && (
              <Button
                size="sm"
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-semibold"
                onClick={() => onReopen && onReopen(job)}
                data-testid={`reopen-job-${job.job_id}`}
              >
                <ArrowRight className="h-3.5 w-3.5 mr-1.5" />
                Reopen Listing
              </Button>
            )}
          </div>
        ) : hasApplied ? (
          <Button className="w-full bg-muted text-muted-foreground cursor-default" disabled>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Applied
          </Button>
        ) : isFilled ? (
          <Button className="w-full" disabled>Positions Filled</Button>
        ) : (
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-lime-500 hover:bg-lime-600 text-slate-900 font-semibold transition-all"
              onClick={() => onApply(job.job_id)}
              disabled={applyingJob === job.job_id}
              data-testid={`apply-job-${job.job_id}`}
            >
              {applyingJob === job.job_id ? 'Applying...' : (
                <>
                  <ArrowRight className="h-4 w-4 mr-2" />
                  Apply Now - ${job.base_rate}/hr
                </>
              )}
            </Button>
            {onAsk && (
              <Button
                variant="outline"
                className="border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10"
                onClick={() => onAsk(job)}
                data-testid={`ask-question-${job.job_id}`}
                title="Ask a question about this listing"
              >
                Ask
              </Button>
            )}
          </div>
        )}
        </div>
      </CardContent>
    </Card>
  );
}

// ===== Partner Dashboard View =====
function PartnerDashboardView({ data, onPostJob, onOpenWallet, onCancelJob, onCompleteJob, positionTypeColor, positionTypeLabel }) {
  if (!data) {
    return (
      <div className="text-center py-16">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  const { wallet, stats, payouts, recent_jobs, recent_transactions } = data;

  const statusColor = (s) =>
    s === 'open' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
    s === 'filled' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' :
    s === 'completed' ? 'bg-lime-500/20 text-lime-400 border-lime-500/30' :
    s === 'cancelled' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
    'bg-slate-500/20 text-muted-foreground border-slate-500/30';

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Available Balance" value={`$${wallet?.balance?.toFixed(2) || '0.00'}`} icon={Wallet} color="indigo" onClick={onOpenWallet} />
        <StatCard label="In Escrow" value={`$${stats?.in_escrow?.toFixed(2) || '0.00'}`} icon={CreditCard} color="amber" />
        <StatCard label="Total Spent" value={`$${wallet?.total_spent?.toFixed(2) || '0.00'}`} icon={TrendingUp} color="slate" />
        <StatCard label="Workers Hired" value={stats?.total_workers_hired || 0} icon={CheckCircle2} color="emerald" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Jobs" value={stats?.total_jobs || 0} icon={Briefcase} color="slate" />
        <StatCard label="Open" value={stats?.open_jobs || 0} icon={Users} color="lime" />
        <StatCard label="Completed" value={stats?.completed_jobs || 0} icon={CheckCircle2} color="emerald" />
        <StatCard label="Pending Payouts" value={`$${payouts?.pending?.total?.toFixed(2) || '0.00'}`} icon={DollarSign} color="amber" />
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        <Button className="bg-indigo-500 hover:bg-indigo-600 font-semibold" onClick={onPostJob} data-testid="partner-post-job">
          <Plus className="h-4 w-4 mr-2" /> Post New Job
        </Button>
        <Button variant="outline" className="border-border text-muted-foreground" onClick={onOpenWallet} data-testid="partner-wallet">
          <Wallet className="h-4 w-4 mr-2" /> Add Funds
        </Button>
      </div>

      {/* Recent Jobs */}
      <div>
        <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-foreground mb-4">Your Job Postings</h3>
        {recent_jobs?.length === 0 ? (
          <div className="text-center py-8 bg-muted/50 rounded-xl border border-border">
            <p className="text-muted-foreground">No jobs posted yet. Get started by posting your first job!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recent_jobs?.map((job) => (
              <div
                key={job.job_id}
                className="bg-card/80 border border-border rounded-xl p-4"
                data-testid={`partner-job-${job.job_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`${positionTypeColor(job.position_type)} text-xs`}>
                      {job.position_label}
                    </Badge>
                    <div>
                      <p className="text-foreground font-semibold text-sm">{job.title}</p>
                      <p className="text-muted-foreground text-xs">
                        {job.location} &middot; {job.date} &middot; {job.slots_filled}/{job.slots_needed} filled
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {job.escrow_amount > 0 && (
                      <span className="text-xs text-amber-400">${job.escrow_amount?.toFixed(2)} escrowed</span>
                    )}
                    <div className="text-right">
                      <p className="text-lime-400 font-semibold text-sm" title="Staff earnings">
                        ${((job.worker_earnings_per_worker ?? (job.base_rate * job.hours)) * job.slots_needed).toFixed(2)}
                      </p>
                      <p className="text-muted-foreground text-xs">Cost: ${job.estimated_total?.toFixed(2)}</p>
                    </div>
                    <Badge className={statusColor(job.status)}>{job.status}</Badge>
                  </div>
                </div>
                {/* Actions for open/filled jobs */}
                {(job.status === 'open' || job.status === 'filled') && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-border/60">
                    {job.slots_filled > 0 && (
                      <Button
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600 text-foreground text-xs"
                        onClick={() => onCompleteJob(job.job_id)}
                        data-testid={`complete-${job.job_id}`}
                      >
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                      onClick={() => onCancelJob(job.job_id)}
                      data-testid={`cancel-${job.job_id}`}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      {recent_transactions?.length > 0 && (
        <div>
          <h3 className="font-['Space_Grotesk'] text-lg font-semibold text-foreground mb-4">Recent Transactions</h3>
          <div className="bg-card/80 border border-border rounded-xl overflow-hidden">
            {recent_transactions.map((txn, i) => (
              <div
                key={txn.txn_id || i}
                className={`px-4 py-3 flex items-center justify-between ${i > 0 ? 'border-t border-border/60' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold ${
                    txn.type === 'deposit' ? 'bg-lime-500/20 text-lime-400' :
                    txn.type === 'escrow_hold' ? 'bg-amber-500/20 text-amber-400' :
                    txn.type === 'escrow_release' ? 'bg-indigo-500/20 text-indigo-400' :
                    txn.type === 'refund' ? 'bg-emerald-500/20 text-emerald-400' :
                    'bg-slate-500/20 text-muted-foreground'
                  }`}>
                    {txn.type === 'deposit' ? '+' : txn.type === 'refund' ? '+' : txn.type === 'escrow_hold' ? 'E' : 'R'}
                  </div>
                  <div>
                    <p className="text-sm text-foreground">{txn.description}</p>
                    <p className="text-xs text-muted-foreground">{new Date(txn.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <span className={`text-sm font-semibold ${txn.amount >= 0 ? 'text-lime-400' : 'text-red-400'}`}>
                  {txn.amount >= 0 ? '+' : ''}{txn.amount !== 0 ? `$${Math.abs(txn.amount).toFixed(2)}` : '--'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Staff Preferences Panel =====
function StaffPreferencesPanel({ prefs, positions, saving, onSave, notifLog }) {
  const [localPrefs, setLocalPrefs] = React.useState(null);

  React.useEffect(() => {
    if (prefs && !localPrefs) {
      setLocalPrefs({ ...prefs });
    }
  }, [prefs, localPrefs]);

  if (!localPrefs) {
    return (
      <div className="bg-card/80 border border-border rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-muted rounded w-48 mb-4" />
        <div className="h-4 bg-muted rounded w-72" />
      </div>
    );
  }

  const allPositions = positions
    ? [...positions.positions.foh, ...positions.positions.boh]
    : [];

  const togglePosition = (key) => {
    setLocalPrefs((prev) => {
      const current = prev.preferred_positions || [];
      return {
        ...prev,
        preferred_positions: current.includes(key)
          ? current.filter((k) => k !== key)
          : [...current, key],
      };
    });
  };

  const togglePositionType = (type) => {
    setLocalPrefs((prev) => {
      const current = prev.preferred_position_types || [];
      return {
        ...prev,
        preferred_position_types: current.includes(type)
          ? current.filter((t) => t !== type)
          : [...current, type],
      };
    });
  };

  return (
    <div className="space-y-5">
      <Card className="bg-card/80 border-border" data-testid="staff-preferences-panel">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              {localPrefs.notifications_enabled ? (
                <div className="h-9 w-9 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                  <Bell className="h-4.5 w-4.5 text-indigo-400" />
                </div>
              ) : (
                <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                  <BellOff className="h-4.5 w-4.5 text-muted-foreground" />
                </div>
              )}
              <div>
                <h3 className="font-['Space_Grotesk'] text-base font-semibold text-foreground">Job Alert Preferences</h3>
                <p className="text-xs text-muted-foreground">Get notified when new jobs match your preferences</p>
              </div>
            </div>
            <button
              onClick={() => setLocalPrefs((p) => ({ ...p, notifications_enabled: !p.notifications_enabled }))}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                localPrefs.notifications_enabled ? 'bg-indigo-500' : 'bg-muted'
              }`}
              data-testid="notif-toggle"
            >
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform duration-200 ${
                  localPrefs.notifications_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {localPrefs.notifications_enabled && (
            <div className="space-y-5 pt-2 border-t border-border">
              {/* Position Types */}
              <div className="pt-4">
                <Label className="text-muted-foreground text-sm mb-2 block">Position Types</Label>
                <div className="flex gap-2">
                  {[
                    { key: 'foh', label: 'Front of House', icon: Wine },
                    { key: 'boh', label: 'Back of House', icon: ChefHat },
                  ].map(({ key, label, icon: Ic }) => {
                    const active = (localPrefs.preferred_position_types || []).includes(key);
                    return (
                      <button
                        key={key}
                        onClick={() => togglePositionType(key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          active
                            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/40'
                            : 'bg-muted text-muted-foreground border border-border hover:bg-accent'
                        }`}
                        data-testid={`pref-type-${key}`}
                      >
                        <Ic className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    );
                  })}
                  {(localPrefs.preferred_position_types || []).length === 0 && (
                    <span className="text-xs text-muted-foreground self-center ml-1">None selected = all types</span>
                  )}
                </div>
              </div>

              {/* Specific Positions */}
              <div>
                <Label className="text-muted-foreground text-sm mb-2 block">Preferred Positions</Label>
                <div className="flex flex-wrap gap-1.5">
                  {allPositions.map((p) => {
                    const active = (localPrefs.preferred_positions || []).includes(p.key);
                    return (
                      <button
                        key={p.key}
                        onClick={() => togglePosition(p.key)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                          active
                            ? 'bg-lime-500/20 text-lime-400 border border-lime-500/40'
                            : 'bg-muted text-muted-foreground border border-border hover:bg-accent'
                        }`}
                        data-testid={`pref-pos-${p.key}`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                  {(localPrefs.preferred_positions || []).length === 0 && (
                    <span className="text-xs text-muted-foreground self-center ml-1">None selected = all positions</span>
                  )}
                </div>
              </div>

              {/* Location & Min Rate */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-sm">Preferred Locations (comma-sep)</Label>
                  <Input
                    value={(localPrefs.preferred_locations || []).join(', ')}
                    onChange={(e) =>
                      setLocalPrefs((p) => ({
                        ...p,
                        preferred_locations: e.target.value
                          .split(',')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      }))
                    }
                    placeholder="e.g. Downtown LA, Hollywood"
                    className="bg-muted border-border text-foreground text-sm"
                    data-testid="pref-locations"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-muted-foreground text-sm">Min Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    value={localPrefs.min_hourly_rate || ''}
                    onChange={(e) =>
                      setLocalPrefs((p) => ({ ...p, min_hourly_rate: parseFloat(e.target.value) || 0 }))
                    }
                    placeholder="0 = no minimum"
                    className="bg-muted border-border text-foreground text-sm"
                    data-testid="pref-min-rate"
                  />
                </div>
              </div>

              <Button
                onClick={() => onSave(localPrefs)}
                disabled={saving}
                className="bg-indigo-500 hover:bg-indigo-600 font-semibold"
                data-testid="save-prefs-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Notifications */}
      {notifLog.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <Mail className="h-3.5 w-3.5" /> Recent Job Alerts Sent to You
          </h4>
          <div className="space-y-2">
            {notifLog.slice(0, 5).map((log) => (
              <div
                key={log.notification_id}
                className="bg-muted/60 border border-border/60 rounded-lg px-4 py-2.5 flex items-center justify-between"
                data-testid={`notif-log-${log.notification_id}`}
              >
                <div className="flex items-center gap-3">
                  <Mail className="h-3.5 w-3.5 text-indigo-400" />
                  <div>
                    <p className="text-sm text-foreground">{log.job_title}</p>
                    <p className="text-xs text-muted-foreground">{log.position_key} &middot; Sent to {log.email}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(log.sent_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Stat Card =====
function StatCard({ label, value, icon: Icon, color, onClick }) {
  return (
    <div
      className={`bg-card/80 border border-border rounded-xl p-4 ${onClick ? 'cursor-pointer hover:border-indigo-500/30 transition-all' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 text-${color}-400`} />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl font-bold text-foreground font-['Space_Grotesk']">{value}</p>
    </div>
  );
}

// ===== Edit Job Dialog =====
function EditJobDialog({ job, token, hostedEvents, onClose, mode = 'edit' }) {
  const isReopen = mode === 'reopen';
  const [form, setForm] = useState({
    title: job.title || '',
    description: job.description || '',
    location: job.location || '',
    date: job.date || '',
    start_time: job.start_time || '',
    end_time: job.end_time || '',
    slots_needed: job.slots_needed || 1,
    requirements: (job.requirements || []).join(', '),
    event_id: job.event_id || '',
    event_name: job.event_name || '',
    custom_position_label: job.custom_position_label || '',
    base_rate: job.base_rate || 0,
    hours: job.hours || 0,
    featured_image_path: job.featured_image_path || '',
    custom_questions: (job.custom_questions || []).map((q) => ({ ...q })),
  });
  const [saving, setSaving] = useState(false);
  const [featuredUploading, setFeaturedUploading] = useState(false);
  const isCustom = job.position_type === 'custom' || job.position_key === 'custom';

  const computedHours = isReopen && form.start_time && form.end_time
    ? Math.max(0, ((new Date(`2000-01-01T${form.end_time}`) - new Date(`2000-01-01T${form.start_time}`)) / 3600000))
    : form.hours;

  const eventismMarkup = job.eventism_markup ?? 5;
  const staffEarnings = (parseFloat(form.base_rate) || 0) * computedHours * (parseInt(form.slots_needed) || 1);
  const yourCost = ((parseFloat(form.base_rate) || 0) + eventismMarkup) * computedHours * (parseInt(form.slots_needed) || 1);

  const handleEventSelect = (val) => {
    if (val === '__none__') {
      setForm({ ...form, event_id: '', event_name: '' });
      return;
    }
    const ev = (hostedEvents || []).find((e) => e.event_id === val);
    setForm({ ...form, event_id: val, event_name: ev?.title || ev?.slug || form.event_name });
  };

  const uploadFeatured = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10 MB)'); return; }
    setFeaturedUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}` } });
      setForm((f) => ({ ...f, featured_image_path: res.data.path }));
      toast.success('Featured image uploaded');
    } catch (e) {
      toast.error('Upload failed');
    } finally { setFeaturedUploading(false); }
  };

  const addQ = (type = 'short_text') => setForm((f) => ({ ...f, custom_questions: [...(f.custom_questions || []), { id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, type, label: '', required: false, options: [] }] }));
  const updateQ = (id, patch) => setForm((f) => ({ ...f, custom_questions: (f.custom_questions || []).map((q) => q.id === id ? { ...q, ...patch } : q) }));
  const removeQ = (id) => setForm((f) => ({ ...f, custom_questions: (f.custom_questions || []).filter((q) => q.id !== id) }));

  const submit = async () => {
    if (!form.title || !form.date || !form.start_time || !form.end_time || !form.location) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        location: form.location,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        slots_needed: parseInt(form.slots_needed) || 1,
        requirements: form.requirements.split(',').map((r) => r.trim()).filter(Boolean),
        event_id: form.event_id || null,
        event_name: form.event_name || null,
        featured_image_path: form.featured_image_path || null,
        custom_questions: form.custom_questions || [],
      };
      if (isCustom) payload.custom_position_label = form.custom_position_label;
      if (isReopen) {
        payload.base_rate = parseFloat(form.base_rate) || 0;
        payload.hours = computedHours;
        await axios.post(`${API}/staffing/jobs/${job.job_id}/reopen`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Job reopened — same link, listing is live again.`);
      } else {
        await axios.patch(`${API}/staffing/jobs/${job.job_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Job updated');
      }
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isReopen ? 'Reopen failed' : 'Update failed'));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-card max-w-2xl" data-testid={isReopen ? 'reopen-job-dialog' : 'edit-job-dialog'}>
        <DialogHeader>
          <DialogTitle>{isReopen ? 'Reopen Job — ' : 'Edit Job — '}{job.position_label}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          {isReopen && (
            <div className="rounded-lg bg-indigo-500/10 border border-indigo-500/30 p-3 text-xs text-indigo-100">
              <strong>Reopening this job</strong> keeps the same listing URL. Prior applicants and any assigned staff will be cleared so you start with a clean review. Escrow will be deducted from your wallet again.
            </div>
          )}
          {isCustom && (
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Custom Role Title *</Label>
              <Input value={form.custom_position_label} onChange={(e) => setForm({ ...form, custom_position_label: e.target.value })} />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Link to an Event (optional)</Label>
            <Select value={form.event_id || '__none__'} onValueChange={handleEventSelect}>
              <SelectTrigger data-testid="edit-select-event"><SelectValue placeholder="Not linked" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__" className="text-muted-foreground">— No event link —</SelectItem>
                {(hostedEvents || []).map((ev) => (
                  <SelectItem key={ev.event_id} value={ev.event_id}>{ev.title || ev.slug || ev.event_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Job Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-input-title" />
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm">Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Location *</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Event Name {form.event_id ? '(auto-filled)' : ''}</Label>
              <Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} disabled={!!form.event_id} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Start *</Label>
              <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">End *</Label>
              <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Slots needed</Label>
              <Input type="number" min="1" value={form.slots_needed} onChange={(e) => setForm({ ...form, slots_needed: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground text-sm">Requirements (comma-separated)</Label>
              <Input value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} placeholder="Black attire, 21+, TIPS certified" />
            </div>
          </div>
          {isReopen && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm">Base hourly rate ($) *</Label>
                <Input type="number" min="0" step="0.5" value={form.base_rate} onChange={(e) => setForm({ ...form, base_rate: e.target.value })} data-testid="reopen-base-rate" />
              </div>
              <div className="rounded-lg bg-lime-500/10 border border-lime-500/30 p-2 text-xs flex flex-col justify-center">
                <div className="flex justify-between"><span className="text-muted-foreground">Staff will earn:</span><span className="text-lime-400 font-semibold">${staffEarnings.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Your total cost (incl. fees):</span><span className="font-semibold">${yourCost.toFixed(2)}</span></div>
              </div>
            </div>
          )}

          {/* Featured image */}
          <div className="space-y-2 pt-2 border-t border-border/40">
            <Label className="text-muted-foreground text-sm">Featured image (optional)</Label>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="file" className="hidden" accept="image/*" onChange={(e) => uploadFeatured(e.target.files?.[0])} data-testid="edit-featured-image-input" disabled={featuredUploading} />
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/30 text-sm">
                  {featuredUploading ? 'Uploading…' : (form.featured_image_path ? 'Replace image' : 'Upload featured image')}
                </span>
              </label>
              {form.featured_image_path && (
                <div className="flex items-center gap-2">
                  <img src={`${API}/files/${form.featured_image_path}`} alt="preview" className="h-14 w-14 object-cover rounded-lg border border-border" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, featured_image_path: '' }))} className="text-xs text-red-400 hover:underline">remove</button>
                </div>
              )}
            </div>
          </div>

          {/* Custom application questions */}
          <div className="space-y-2 pt-2 border-t border-border/40">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-muted-foreground text-sm">Custom application questions</Label>
                <p className="text-[11px] text-muted-foreground">Edit, mark required, add or remove. Existing applicants' answers stay attached.</p>
              </div>
              <Select onValueChange={addQ}>
                <SelectTrigger className="w-44 h-9 bg-muted border-border text-foreground text-xs" data-testid="edit-add-question">
                  <SelectValue placeholder="+ Add question" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short_text">Short text</SelectItem>
                  <SelectItem value="long_text">Long text</SelectItem>
                  <SelectItem value="url">URL / link</SelectItem>
                  <SelectItem value="file">File / photo upload</SelectItem>
                  <SelectItem value="yes_no">Yes / no</SelectItem>
                  <SelectItem value="dropdown">Dropdown</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(form.custom_questions || []).map((q, idx) => (
              <div key={q.id} className="bg-muted/40 border border-border/40 rounded-lg p-3 space-y-2" data-testid={`edit-cq-row-${idx}`}>
                <div className="flex items-start gap-2">
                  <Input value={q.label} onChange={(e) => updateQ(q.id, { label: e.target.value })} placeholder="Question label" className="bg-card border-border text-foreground flex-1" data-testid={`edit-cq-label-${idx}`} />
                  <Badge variant="outline" className="text-xs whitespace-nowrap mt-2">{(q.type || '').replace('_', ' ')}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => removeQ(q.id)} className="text-red-400 mt-1" data-testid={`edit-cq-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                {q.type === 'dropdown' && (
                  <Input value={(q.options || []).join(', ')} onChange={(e) => updateQ(q.id, { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })} placeholder="Comma-separated options" className="bg-card border-border text-foreground" />
                )}
                <Input value={q.help_text || ''} onChange={(e) => updateQ(q.id, { help_text: e.target.value })} placeholder="Help text (optional)" className="bg-card border-border text-foreground text-xs" />
                <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!q.required} onChange={(e) => updateQ(q.id, { required: e.target.checked })} data-testid={`edit-cq-required-${idx}`} />
                  Required
                </label>
              </div>
            ))}
          </div>

          <p className="text-[11px] text-muted-foreground">
            {isReopen
              ? `On reopen, $${yourCost.toFixed(2)} will be held in escrow from your Eventism wallet.`
              : "Note: Rate & escrow can't be edited once posted. Cancel & repost to change the rate."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className={isReopen ? 'bg-emerald-500 hover:bg-emerald-600 text-slate-900' : 'bg-indigo-500 hover:bg-indigo-600'} data-testid={isReopen ? 'reopen-job-save' : 'edit-job-save'}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Pencil className="h-4 w-4 mr-1" />}
            {isReopen ? 'Reopen listing' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

